import * as React from 'react';
import { ActionRequest } from '../../actions';
import { Card, CardContent, Grid2 as Grid, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { Loading, Table } from '../common';
import { Moment } from 'moment/moment';
import { getFlagIcon, truncateNumber } from '../../utils';
import moment from 'moment';
import { ReportFilterDates } from './ReportFilterDates';

interface Props {
    readonly styled: StyledProps;
}

type UserType = 'Active' | 'New';
interface State {
    readonly loading: boolean;
    readonly startTimeSpam: Moment;
    readonly endTimeSpam: Moment;
    readonly userType: UserType;
    readonly data?: AnalyticsModels.CountryTotalUsersReport[];
}

export class UserByCountryReport extends React.PureComponent<Props, State> {
    state: State = {
        loading: true,
        startTimeSpam: moment.utc().subtract(7, 'days'),
        endTimeSpam: moment.utc(),
        userType: 'Active',
    };

    componentDidMount() {
        this.getInitialData().then();
    }

    render() {
        const { styled } = this.props;
        const { loading, startTimeSpam, endTimeSpam, userType, data } = this.state;
        const selectValue = userType === 'Active' ? 0 : 1;

        const onChangeMethod = (event: SelectChangeEvent<number>) => {
            if (event.target.value === 0) {
                this.setState({ userType: 'Active' });
                this.getUserByCountry().then();
            } else if (event.target.value === 1) {
                this.setState({ userType: 'New' });
                this.getUserByCountry().then();
            }
        }

        const onFilterDates = (start: Moment, end: Moment) => {
            this.setState({ startTimeSpam: start, endTimeSpam: end});
            this.getInitialData(start, end).then();
        };

        return (
            <Card style={{ padding: 0 }}>
                <CardContent style={{ padding: 10 }}>
                    {loading && !data && (
                        <div style={{ height: 352 }}>
                            <Loading styled={this.props.styled} />
                        </div>
                    )}
                    {!loading && data && (
                        <Grid container={true} justifyContent="center" spacing={0}>
                            <Grid size={12}>
                                <div style={{ color: styled.themeMode === 'light' ? '#5f6368' : '#8a8e93', letterSpacing: '.5px', fontSize: 14, fontWeight: 500, textUnderlinePosition: 'under', textUnderlineOffset: 1, textDecorationLine: 'underline', textDecorationStyle: 'dashed' }}>
                                    <Select
                                        value={selectValue}
                                        onChange={onChangeMethod}
                                        sx={{
                                            width: 150,
                                            '&.MuiOutlinedInput-root': {
                                                boxShadow: 'none !important',
                                                lineHeight: 18,
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                fontSize: 14,
                                                fontWeight: 500,
                                            },
                                        }}
                                    >
                                        <MenuItem key={0} value={0}>
                                            Active users
                                        </MenuItem>
                                        <MenuItem key={1} value={1}>
                                            New users
                                        </MenuItem>
                                    </Select>
                                    BY COUNTRY
                                </div>
                            </Grid>
                            <Grid size={12}>
                                <Table
                                    styled={this.props.styled}
                                    columns={[
                                        {
                                            title: 'COUNTRY',
                                            field: 'country',
                                            render: (rowData: AnalyticsModels.CountryTotalUsersReport) => `${getFlagIcon(rowData.countryId)} ${rowData.country}`,
                                        },
                                        {
                                            title: userType === 'Active' ? 'ACTIVE USERS' : 'NEW USERS',
                                            field: 'totalUsers',
                                            render: (rowData: AnalyticsModels.CountryTotalUsersReport) => `${truncateNumber(rowData.totalUsers)}`,
                                            cellStyle: {
                                                textAlign: 'right',
                                            },
                                            headerStyle: {
                                                textAlign: 'right',
                                            }
                                        },
                                    ]}
                                    data={data}
                                    options={{
                                        selection: false,
                                        paging: false,
                                        emptyRowsWhenPaging: false,
                                        search: false,
                                        sorting: false,
                                        draggable: false,
                                        showTitle: false,
                                        toolbar: false,
                                    }}
                                    showBox={false}
                                />
                            </Grid>
                            <Grid size={12}>
                                <ReportFilterDates
                                    styled={this.props.styled}
                                    start={startTimeSpam}
                                    end={endTimeSpam}
                                    onFilter={onFilterDates}
                                />
                            </Grid>
                        </Grid>
                    )}
                </CardContent>
            </Card>
        );
    }

    private async getInitialData(start?: Moment, end?: Moment): Promise<void> {
        await this.getUserByCountry(start, end);
    }

    private async getUserByCountry(start?: Moment, end?: Moment): Promise<void> {
        const { userType } = this.state;
        this.setState({
            loading: true,
            data: undefined,
        });
        const startTimeSpam = start || this.state.startTimeSpam;
        const endTimeSpam = end || this.state.endTimeSpam;
        const metric = userType === 'Active' ? 'activeUsers' : 'newUsers';
        const url = `analytics/usersByCountryReport`;
        const data = await ActionRequest.get<AnalyticsModels.CountryTotalUsersReport[]>(url, {
            startDate: startTimeSpam.format('YYYY-MM-DD'),
            endDate: endTimeSpam.format('YYYY-MM-DD'),
            metric,
        });
        this.setState({
            loading: false,
            data: data || undefined,
        });
    }
}